import { FC, useEffect, useState } from "react";
import {
  getSuppliers,
  importSuppliers,
  querySuppliers,
} from "../../app/services/supplierService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { Supplier } from "../../models/responses/supplier";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { Badge, Col, Pagination, Row, Spinner } from "react-bootstrap";
import { BoxArrowInDown, Eye, Plus, Check, X } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

interface Props {}

const Suppliers: FC<Props> = () => {
  const reloadSupplierList: boolean = useSelector(
    (state: RootState) => state.supplier.shouldUpdateSupplierList
  );
  const runningSupplierImport: boolean = useSelector(
    (state: RootState) => state.supplier.runningSupplierImport
  );
  const suppliers: Supplier[] = useSelector(
    (state: RootState) => state.supplier.querySupplierList || []
  );
  const loadingSuppliers = useSelector(
    (state: RootState) => state.supplier.isWaitingSupplierGetResponse
  );
  const importedSuppliersCount = useSelector(
    (state: RootState) => state.supplier.importedSuppliersCount
  );
  const [hasImportedNewSuppliers, setHasImportedNewSuppliers] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const total = useSelector(
    (state: RootState) => state.supplier.totalQuerySupplier
  );
  const totalPages = Math.ceil((total || 0) / pageSize);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  useEffect(() => {
    dispatch(querySuppliers(instance, account, page, pageSize));
  }, [account, accounts, dispatch, instance, page, pageSize]);

  useEffect(() => {
    if (reloadSupplierList) {
      setHasImportedNewSuppliers(true);
      dispatch(getSuppliers(instance, account, null));
    }
  }, [reloadSupplierList, instance, account, dispatch]);

  const ImportSuppliers = () => {
    dispatch(importSuppliers(instance, account));
  };

  const openSupplierHandler = (supplier: Supplier) => {
    navigate(`/suppliers/${supplier.id}`, { relative: "path" });
  };

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h1>Leverantörer</h1>
        </Col>
        <Col className="text-end">
          {hasImportedNewSuppliers && (
            <Badge className="me-2 mt-2">
              {importedSuppliersCount} uppdaterade från AX
            </Badge>
          )}
          <Button
            className="mt-2"
            variant="outline-primary"
            onClick={ImportSuppliers}
            disabled={runningSupplierImport}
          >
            {!runningSupplierImport ? (
              <span>
                Importera leverantörer från AX <BoxArrowInDown />
              </span>
            ) : (
              <span>
                Importerar... <Spinner animation="border" size="sm" />
              </span>
            )}
          </Button>
          <Button
            className="mt-2 ms-2"
            variant="outline-primary"
            href={`/suppliers/add`}
          >
            Lägg till <Plus />
          </Button>
        </Col>
      </Row>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Lev.nr (AX)</th>
            <th>Namn</th>
            <th>Skadeverkstad</th>
            <th>AD Butik</th>
            <th>Fleet</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {suppliers.map((supplier) => (
            <tr
              key={supplier.id}
              role="button"
              onClick={() => openSupplierHandler(supplier)}
            >
              <td>{supplier.externalId}</td>
              <td>{supplier.name}</td>
              <td>
                {!supplier.clientTypeStandard ? (
                  <X className="text-danger" />
                ) : (
                  <Check className="text-success" />
                )}
              </td>
              <td>
                {!supplier.clientTypeCatalog ? (
                  <X className="text-danger" />
                ) : (
                  <Check className="text-success" />
                )}
              </td>
              <td>
                {!supplier.clientTypeFleet ? (
                  <X className="text-danger" />
                ) : (
                  <Check className="text-success" />
                )}
              </td>
              <td align="center">
                <Eye />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {loadingSuppliers && (
        <>
          <Row className="mb-3">
            <Col className="text-center">
              <Spinner variant="primary" />
            </Col>
          </Row>
        </>
      )}
      <Pagination className="justify-content-center">
        <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
        <Pagination.Prev
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
        />
        {[...Array(totalPages)].map((_, index) => {
          const pageIndex = index + 1;
          if (pageIndex >= page - 3 && pageIndex <= page + 3) {
            return (
              <Pagination.Item
                key={pageIndex}
                active={pageIndex === page}
                onClick={() => setPage(pageIndex)}
              >
                {pageIndex}
              </Pagination.Item>
            );
          }
          return null;
        })}
        <Pagination.Next
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
        />
        <Pagination.Last
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
        />
      </Pagination>
    </div>
  );
};

export default Suppliers;
